// Option<T> — FP null elimination. No null/undefined in domain logic.

export type Some<T> = Readonly<{
  readonly some: true;
  readonly value: T;
}>;

export type None = Readonly<{
  readonly some: false;
}>;

export type Option<T> = Some<T> | None;

const NONE: None = Object.freeze({ some: false });

export const some = <T>(value: T): Some<T> => Object.freeze({ some: true, value });

export const none = (): None => NONE;

export const fromNullable = <T>(value: T | null | undefined): Option<T> =>
  value != null ? some(value) : none();

export const map = <T, U>(option: Option<T>, fn: (value: T) => U): Option<U> =>
  option.some ? some(fn(option.value)) : option;

export const flatMap = <T, U>(
  option: Option<T>,
  fn: (value: T) => Option<U>,
): Option<U> => (option.some ? fn(option.value) : option);

export const unwrapOr = <T>(option: Option<T>, fallback: T): T =>
  option.some ? option.value : fallback;

export const isSome = <T>(option: Option<T>): option is Some<T> => option.some;

export const isNone = <T>(option: Option<T>): option is None => !option.some;

export const filter = <T>(
  option: Option<T>,
  predicate: (value: T) => boolean,
): Option<T> => (option.some && predicate(option.value) ? option : none());
